import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, Checkbox, CircularProgress, Link, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import CirActionBar from '../components/e2i/CirActionBar';
import { StatusChip } from '../components/e2i/StatusChip';
import useCompanyId from '../components/e2i/useCompanyId';
import type { Notify } from '../components/e2i/workspace';
import e2iApi from '../services/e2iApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { BulkSubmitResult, Cir, CirStatus, E2IConfig } from '../types/e2i';
import { CIR_STATUS } from '../types/e2i';
import { draftLabel, errorText, formatAmount, formatDate, plural } from '../utils/e2i';

type Filter = 'Open' | 'Submitted' | 'Booked' | 'Posted' | 'All';
const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'Open', label: 'Open' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Booked', label: 'Booked' },
  { value: 'Posted', label: 'Posted' },
  { value: 'All', label: 'All' },
];

/**
 * Enquiry to Invoice › Client Invoice Requests: the checklist across enquiries. The team submits requests (one by
 * one or selected together); finance books them as SAP A/R invoice drafts or returns them; after posting the team
 * records when the invoice reached the client.
 */
const E2IInvoiceRequests: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { companyId, companyName } = useCompanyId();
  const [config, setConfig] = useState<E2IConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState<Cir[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkSubmitResult | null>(null);
  const [tick, setTick] = useState(0);

  const notify: Notify = useCallback((message, severity = 'success') => {
    dispatch(showSnackbar({ message, severity }));
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); setConfig(null); return undefined; }
    let cancelled = false;
    setError(null);
    setConfig(null);
    e2iApi.config(companyId)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        // Finance lands on what waits for it to book; the team on what it has to submit.
        setFilter((f) => f || (c.capabilities.finance ? 'Submitted' : 'Open'));
      })
      .catch((e: any) => { if (!cancelled) setError(errorText(e, 'Enquiry to Invoice could not be opened for this company')); });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !config || !filter) return undefined;
    let cancelled = false;
    setLoading(true);
    setListError(null);
    e2iApi.listCirs(companyId, filter === 'All' ? undefined : filter, debounced || undefined)
      .then((r) => { if (!cancelled) { setRows(r); setSelected([]); } })
      .catch((e: any) => { if (!cancelled) { setRows([]); setListError(errorText(e, 'The invoice requests could not be loaded')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, config, filter, debounced, tick]);

  const currency = config?.currency || '';
  const submittable = useMemo(() => rows.filter((r) => r.permissions?.canSubmit), [rows]);
  const allTicked = submittable.length > 0 && submittable.every((r) => selected.includes(r.cirId));

  const replaceRow = useCallback((next: Cir) => {
    setRows((rs) => rs.map((r) => (r.cirId === next.cirId ? next : r)));
    setSelected((s) => (next.permissions?.canSubmit ? s : s.filter((x) => x !== next.cirId)));
  }, []);

  const toggle = useCallback((id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])), []);

  const submitSelected = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const result = await e2iApi.bulkSubmitCirs(companyId, selected);
      setBulkResult(result);
      if (result.submitted.length) notify(`${plural(result.submitted.length, 'invoice request')} submitted to finance`, result.failed.length ? 'warning' : 'success');
      else if (result.failed.length) notify('No invoice request could be submitted', 'error');
      setTick((t) => t + 1);
    } catch (e: any) {
      notify(errorText(e, 'The invoice requests could not be submitted'), 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const openEnquiry = useCallback((r: Cir) => navigate(`/e2i/enquiries/${r.enquiryId}?tab=cirs`), [navigate]);

  const columns: ResponsiveTableColumn<Cir>[] = useMemo(() => [
    {
      id: 'select',
      header: (
        <Checkbox
          size="small"
          checked={allTicked}
          indeterminate={!allTicked && selected.length > 0}
          onChange={() => setSelected(allTicked ? [] : submittable.map((r) => r.cirId))}
          disabled={!submittable.length}
          inputProps={{ 'aria-label': 'Select every request that can be submitted' }}
        />
      ),
      width: 44,
      cell: (r) => (r.permissions?.canSubmit ? (
        <Checkbox size="small" checked={selected.includes(r.cirId)} onChange={() => toggle(r.cirId)} inputProps={{ 'aria-label': `Select ${r.cirNo || 'request'} to submit` }} />
      ) : null),
    },
    {
      id: 'no',
      header: 'Invoice request',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`/e2i/enquiries/${r.enquiryId}?tab=cirs`} underline="hover" fontWeight={600}>{r.cirNo || 'Draft'}</Link>
          <Typography variant="caption" color="text.secondary" display="block">{[r.enquiryNo, r.jobNo].filter(Boolean).join(' · ')}</Typography>
        </Box>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.customer?.name || r.customer?.code}</Typography>
          <Typography variant="caption" color="text.secondary">{r.customerPoRef ? `PO ${r.customerPoRef}` : 'No client PO'}{r.customerTrn ? ` · VAT ${r.customerTrn}` : ''}</Typography>
        </Box>
      ),
    },
    {
      id: 'period',
      header: 'Service period',
      hideOnMobile: true,
      cell: (r) => (r.periodFrom || r.periodTo ? `${formatDate(r.periodFrom) || '…'} to ${formatDate(r.periodTo) || '…'}` : '—'),
    },
    {
      id: 'total',
      header: `Total ${currency}`.trim(),
      align: 'right',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{formatAmount(r.total)}</Typography>
          <Typography variant="caption" color="text.secondary">VAT {formatAmount(r.vatAmount)}</Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <StatusChip status={CIR_STATUS[r.status as CirStatus]} fallback={r.status} />
          {r.sap?.invoiceNum ? <Typography variant="caption">Invoice {r.sap.invoiceNum}{r.sap.invoiceDate ? ` · ${formatDate(r.sap.invoiceDate)}` : ''}</Typography> : null}
          {!r.sap?.invoiceNum && r.sap?.draftEntry ? <Typography variant="caption">Draft {draftLabel(r.sap.draftNum, r.sap.draftEntry)}</Typography> : null}
          {r.sap?.error && r.status !== 'Posted' ? <Typography variant="caption" color="error">SAP: {r.sap.error}</Typography> : null}
          {r.returnNote && r.status === 'Open' ? <Typography variant="caption" sx={{ color: '#7a3e00' }}>Returned: {r.returnNote}</Typography> : null}
          {r.clientSubmissionDate ? <Typography variant="caption" color="text.secondary">To client {formatDate(r.clientSubmissionDate)}</Typography> : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (r) => <CirActionBar cir={r} onChanged={replaceRow} onEdit={() => openEnquiry(r)} editLabel="Open to edit" notify={notify} />,
    },
  ], [allTicked, currency, notify, openEnquiry, replaceRow, selected, submittable, toggle]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1500 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Client invoice requests</Typography>
        {companyName ? <Typography variant="body2" color="text.secondary">{companyName}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => setTick((t) => t + 1)} disabled={loading || !config}>Refresh</Button>
        {submittable.length ? (
          <Button size="small" onClick={() => { void submitSelected(); }} disabled={bulkBusy || !selected.length}>
            {bulkBusy ? 'Submitting…' : selected.length ? `Submit ${plural(selected.length, 'request')}` : 'Submit selected'}
          </Button>
        ) : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {config && !config.enabled ? <Alert severity="warning">Enquiry to Invoice is not switched on for {companyName || 'this company'} (Company Management).</Alert> : null}
      {bulkResult && bulkResult.failed.length ? (
        <Alert severity="warning" onClose={() => setBulkResult(null)}>
          {plural(bulkResult.failed.length, 'request')} could not be submitted:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {bulkResult.failed.map((f) => (
              <li key={f.cirId}>{rows.find((r) => r.cirId === f.cirId)?.cirNo || `Request ${f.cirId}`}: {f.message}</li>
            ))}
          </Box>
        </Alert>
      ) : null}

      {config ? (
        <>
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Tabs value={filter || false} onChange={(_e, v) => setFilter(v)} aria-label="Which invoice requests" variant="scrollable" allowScrollButtonsMobile sx={{ flex: '1 1 auto' }}>
                {FILTERS.map((f) => <Tab key={f.value} value={f.value} label={f.label} />)}
              </Tabs>
              <TextField id="e2i-cir-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Number, customer, PO, enquiry or job" sx={{ minWidth: { md: 320 } }} />
            </Stack>
          </Paper>
          {listError ? <Alert severity="error">{listError}</Alert> : null}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
          ) : (
            <ResponsiveTable
              ariaLabel="Client invoice requests"
              columns={columns}
              rows={rows}
              getRowKey={(r) => r.cirId}
              emptyMessage={filter === 'Submitted' ? 'Nothing waits for finance' : filter === 'Open' ? 'No open invoice requests' : 'No invoice requests'}
            />
          )}
        </>
      ) : !error ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
      ) : null}
    </Box>
  );
};

export default E2IInvoiceRequests;
